import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { normalizeEmail, toDate } from '../lib/format';
import { Download, Project, ProjectMember, ProjectStatus, Release } from '../types';
import { accessService } from './accessService';
import { storageService } from './storageService';

const COLLECTIONS = {
  PROJECTS: 'projects',
  MEMBERS: 'projectMembers',
  RELEASES: 'releases',
  DOWNLOADS: 'downloads',
  FAVORITES: 'favorites',
};

export const memberDocId = (projectId: string, email: string) => `${projectId}_${normalizeEmail(email)}`;

const toProject = (id: string, data: any): Project => ({
  id,
  name: data.name || '',
  description: data.description || '',
  createdBy: data.createdBy,
  status: data.status || 'active',
  createdAt: toDate(data.createdAt) || new Date(0),
  updatedAt: toDate(data.updatedAt) || toDate(data.createdAt) || new Date(0),
});

const toRelease = (id: string, data: any): Release => ({
  id,
  projectId: data.projectId,
  version: data.version || '',
  title: data.title || '',
  description: data.description || '',
  fileName: data.fileName || '',
  fileSize: data.fileSize || 0,
  fileUrl: data.fileUrl || '',
  uploadedBy: data.uploadedBy,
  uploadedAt: toDate(data.uploadedAt) || new Date(),
});

const toDownload = (id: string, data: any): Download => ({
  id,
  releaseId: data.releaseId,
  projectId: data.projectId,
  downloadedBy: data.downloadedBy,
  downloadedByEmail: data.downloadedByEmail || '',
  downloadedByName: data.downloadedByName || '',
  downloadedAt: toDate(data.downloadedAt) || new Date(),
  ipAddress: data.ipAddress,
});

const toMember = (id: string, data: any): ProjectMember => ({
  id,
  projectId: data.projectId,
  name: data.name || data.email,
  email: data.email,
  role: data.role === 'owner' ? 'owner' : 'member',
  addedBy: data.addedBy,
  addedAt: toDate(data.addedAt) || new Date(),
});

const byDateDesc = <T,>(pick: (item: T) => Date) => (a: T, b: T) => pick(b).getTime() - pick(a).getTime();

export class AlreadyMemberError extends Error {
  code = 'already-member';
}

export const projectService = {
  // ---------- projects ----------
  async createProject(name: string, description: string, userId: string): Promise<string> {
    const projectRef = await addDoc(collection(db, COLLECTIONS.PROJECTS), {
      name: name.trim(),
      description: description.trim(),
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'active',
    });
    return projectRef.id;
  },

  async getProject(projectId: string): Promise<Project | null> {
    try {
      const snap = await getDoc(doc(db, COLLECTIONS.PROJECTS, projectId));
      return snap.exists() ? toProject(snap.id, snap.data()) : null;
    } catch (error: any) {
      if (error?.code === 'permission-denied') return null;
      throw error;
    }
  },

  async getUserProjects(userId: string): Promise<Project[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.PROJECTS), where('createdBy', '==', userId), orderBy('createdAt', 'desc'))
    );
    return snapshot.docs.map((d) => toProject(d.id, d.data()));
  },

  async getAllProjects(): Promise<Project[]> {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.PROJECTS), orderBy('createdAt', 'desc')));
    return snapshot.docs.map((d) => toProject(d.id, d.data()));
  },

  /** Projects the user was added to as a member (excluding deleted ones). */
  async getProjectsWhereUserIsMember(userEmail: string): Promise<Project[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.MEMBERS), where('email', '==', normalizeEmail(userEmail)))
    );
    const ids = Array.from(new Set(snapshot.docs.map((d) => d.data().projectId as string)));
    const projects = await Promise.all(ids.map((id) => this.getProject(id)));
    return projects.filter((p): p is Project => !!p && p.status !== 'deleted');
  },

  async updateProject(projectId: string, updates: Partial<Pick<Project, 'name' | 'description'>>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.PROJECTS, projectId), { ...updates, updatedAt: serverTimestamp() });
  },

  async setProjectStatus(projectId: string, status: ProjectStatus): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.PROJECTS, projectId), { status, updatedAt: serverTimestamp() });
  },

  // ---------- members ----------
  /**
   * Adds a member and puts their address on the invitation list, so they can sign in.
   * Returns the member document id (used by the e-mail service).
   */
  async addMember(
    projectId: string,
    name: string,
    email: string,
    addedBy: string,
    role: 'owner' | 'member' = 'member'
  ): Promise<string> {
    const normalized = normalizeEmail(email);
    const id = memberDocId(projectId, normalized);
    const ref = doc(db, COLLECTIONS.MEMBERS, id);

    const existing = await getDoc(ref);
    if (existing.exists()) throw new AlreadyMemberError('Already a member');

    await accessService.invite(normalized, addedBy, { projectId });
    await setDoc(ref, {
      projectId,
      name: name.trim(),
      email: normalized,
      role,
      addedBy,
      addedAt: serverTimestamp(),
    });
    return id;
  },

  async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.MEMBERS), where('projectId', '==', projectId)));
    return snapshot.docs
      .map((d) => toMember(d.id, d.data()))
      .sort((a, b) => a.addedAt.getTime() - b.addedAt.getTime());
  },

  async removeMember(memberId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.MEMBERS, memberId));
  },

  /**
   * Moves members stored before the security update (random ids, mixed-case
   * e-mails) to `${projectId}_${email}` ids and makes sure they are invited.
   * Returns how many documents were migrated.
   */
  async migrateLegacyMembers(projectId: string, actingUid: string): Promise<number> {
    const members = await this.getProjectMembers(projectId);
    let migrated = 0;
    for (const member of members) {
      const email = normalizeEmail(member.email || '');
      if (!email) continue;
      const targetId = memberDocId(projectId, email);
      await accessService.invite(email, actingUid, { projectId });
      if (member.id === targetId) continue;
      const target = await getDoc(doc(db, COLLECTIONS.MEMBERS, targetId));
      if (target.exists()) {
        // Already migrated (or a duplicate entry): just drop the legacy copy.
        await deleteDoc(doc(db, COLLECTIONS.MEMBERS, member.id));
        migrated++;
        continue;
      }
      await setDoc(doc(db, COLLECTIONS.MEMBERS, targetId), {
        projectId,
        name: member.name,
        email,
        role: member.role,
        addedBy: member.addedBy || actingUid,
        addedAt: member.addedAt,
      });
      await deleteDoc(doc(db, COLLECTIONS.MEMBERS, member.id));
      migrated++;
    }
    return migrated;
  },

  // ---------- releases ----------
  async createRelease(
    projectId: string,
    version: string,
    title: string,
    description: string,
    fileName: string,
    fileSize: number,
    fileUrl: string,
    uploadedBy: string
  ): Promise<string> {
    const releaseRef = await addDoc(collection(db, COLLECTIONS.RELEASES), {
      projectId,
      version: version.trim(),
      title: title.trim(),
      description: description.trim(),
      fileName,
      fileSize,
      fileUrl,
      uploadedBy,
      uploadedAt: serverTimestamp(),
    });
    return releaseRef.id;
  },

  async getProjectReleases(projectId: string): Promise<Release[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.RELEASES), where('projectId', '==', projectId), orderBy('uploadedAt', 'desc'))
    );
    return snapshot.docs.map((d) => toRelease(d.id, d.data()));
  },

  /** Deletes the release record and its stored file. */
  async deleteRelease(release: Release): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.RELEASES, release.id));
    if (release.fileUrl) {
      try {
        await storageService.deleteFile(release.fileUrl);
      } catch (error) {
        console.warn('Release deleted, but its file could not be removed from storage:', error);
      }
    }
  },

  async updateRelease(
    releaseId: string,
    updates: Partial<Pick<Release, 'version' | 'title' | 'description'>>
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.RELEASES, releaseId), { ...updates, updatedAt: serverTimestamp() });
  },

  // ---------- downloads ----------
  async recordDownload(
    releaseId: string,
    projectId: string,
    downloadedBy: string,
    downloadedByEmail: string,
    downloadedByName: string,
    ipAddress?: string
  ): Promise<string> {
    const downloadRef = await addDoc(collection(db, COLLECTIONS.DOWNLOADS), {
      releaseId,
      projectId,
      downloadedBy,
      downloadedByEmail,
      downloadedByName,
      downloadedAt: serverTimestamp(),
      ...(ipAddress ? { ipAddress } : {}),
    });
    return downloadRef.id;
  },

  /**
   * Download log of a release. Managers see everyone; other members only their own entries.
   * (Equality-only queries, so no extra composite indexes are needed.)
   */
  async getReleaseDownloads(release: Pick<Release, 'id' | 'projectId'>, onlyUserId?: string): Promise<Download[]> {
    const q = onlyUserId
      ? query(
          collection(db, COLLECTIONS.DOWNLOADS),
          where('releaseId', '==', release.id),
          where('downloadedBy', '==', onlyUserId)
        )
      : query(
          collection(db, COLLECTIONS.DOWNLOADS),
          where('projectId', '==', release.projectId),
          where('releaseId', '==', release.id)
        );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => toDownload(d.id, d.data())).sort(byDateDesc((d) => d.downloadedAt));
  },

  async getProjectDownloads(projectId: string): Promise<Download[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.DOWNLOADS), where('projectId', '==', projectId), orderBy('downloadedAt', 'desc'))
    );
    return snapshot.docs.map((d) => toDownload(d.id, d.data()));
  },

  // ---------- favorites ----------
  async addFavorite(projectId: string, userId: string): Promise<void> {
    const existing = await getDocs(
      query(collection(db, COLLECTIONS.FAVORITES), where('userId', '==', userId), where('projectId', '==', projectId))
    );
    if (!existing.empty) return;
    await addDoc(collection(db, COLLECTIONS.FAVORITES), { projectId, userId, favoritedAt: serverTimestamp() });
  },

  async removeFavorite(projectId: string, userId: string): Promise<void> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.FAVORITES), where('userId', '==', userId), where('projectId', '==', projectId))
    );
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  },

  async getUserFavoriteProjectIds(userId: string): Promise<Set<string>> {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.FAVORITES), where('userId', '==', userId)));
    return new Set(snapshot.docs.map((d) => d.data().projectId as string));
  },
};
